import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';

import {
  ApolloError,
  MutationHookOptions,
  MutationTuple,
} from '@apollo/client';

import { alertApiRef, useApi } from '@backstage/core-plugin-api';

import { GraphQLAppError } from './GraphQLAppError';
import { GraphQLResult, getError, getTypeOrNull, isError } from './apollo';
import { type Error as AppError } from './graphql';

// Options that can be passed when using the hook
export type StandardCreateHookOptions<
  TResult,
  TData = unknown,
  TVariables = unknown,
> = {
  onComplete?: (result: TResult) => void;
  onError?: 'post-alert' | ((error: Error | ApolloError) => void);
  update?: MutationHookOptions<TData, TVariables>['update'];
  optimisticResponse?: MutationHookOptions<
    TData,
    TVariables
  >['optimisticResponse'];
};

// The result type returned by the hook
export type StandardCreateHookResult<
  TResult,
  TPath extends string,
  TArgs extends any[] = any[],
> = {
  error?: Error | ApolloError;
  isCreating: boolean;
  called: boolean;
  result?: TResult;
} & {
  // The create function that takes rest parameters and returns a promise of the result
  [K in TPath]: (...args: TArgs) => Promise<TResult>;
};

export function withStandardCreateHook<
  // TData is the full mutation result type
  TData extends {
    [K in TPath]?:
      | TResult
      | { __typename: 'Error'; message?: string | null }
      | null;
  },
  // TResult is the expected result type when successful
  TResult extends GraphQLResult | null,
  // TPath is the key in the mutation result where the data lives
  TPath extends string,
  // TVariables is the mutation variables type - can be any shape
  TVariables,
  // TArgs is a tuple type representing the arguments
  TArgs extends any[],
>(
  useMutation: (
    options?: MutationHookOptions<TData, TVariables>,
  ) => MutationTuple<TData, TVariables>,
  dataPath: TPath,
  // Transform function is required since we can't make assumptions about variables shape
  transformInput: (...args: TArgs) => TVariables,
  // Mutation options are optional and can be used to customize the mutation
  mutationOptions?: Pick<
    MutationHookOptions<TData, TVariables>,
    'awaitRefetchQueries' | 'refetchQueries' | 'update' | 'optimisticResponse'
  >,
) {
  return (
    options: StandardCreateHookOptions<TResult, TData, TVariables> = {},
  ): StandardCreateHookResult<TResult, TPath, TArgs> => {
    const alert = useApi(alertApiRef);
    const [
      mutate,
      { loading: isCreating, error: mutationError, data, called },
    ] = useMutation(mutationOptions);

    // we use a ref to avoid recreating the create callback when update changes
    const updateRef = useRef(options.update);
    useLayoutEffect(() => {
      updateRef.current = options.update;
    }, [options.update]);

    // we use a ref to avoid recreating the create callback when optimisticResponse changes
    const optimisticResponseRef = useRef(options.optimisticResponse);
    useLayoutEffect(() => {
      optimisticResponseRef.current = options.optimisticResponse;
    }, [options.optimisticResponse]);

    const create = useCallback(
      async (...args: TArgs): Promise<TResult> => {
        const variables = transformInput(...args);
        const response = await mutate({
          variables,
          update: updateRef.current,
          optimisticResponse: optimisticResponseRef.current,
        });
        const result = response.data?.[dataPath];

        if (isError(result)) {
          const appError = result as AppError;
          throw new GraphQLAppError(
            appError.message ?? 'Unknown error',
            appError.code,
          );
        }

        const typedResult = result as TResult;
        return typedResult;
      },
      [mutate],
    );

    const result = data?.[dataPath];
    const typedResult = getTypeOrNull(result) as TResult | undefined;

    const error = useMemo(() => {
      if (mutationError) return mutationError;
      const appError = getError(result);
      if (appError) return appError;
      return undefined;
    }, [mutationError, result]);

    // we use a ref to avoid that the onComplete is called on every render
    // if its not stable.
    const onCompleteValue = useRef(options.onComplete);
    useLayoutEffect(() => {
      onCompleteValue.current = options.onComplete;
    }, [options.onComplete]);

    useEffect(() => {
      if (typedResult && onCompleteValue.current) {
        onCompleteValue.current(typedResult);
      }
    }, [typedResult]);

    // we use a ref to avoid that the onError is called on every render
    // if its not stable.
    const onErrorValue = useRef(options.onError);
    useLayoutEffect(() => {
      onErrorValue.current = options.onError;
    }, [options.onError]);

    useEffect(() => {
      if (error) {
        if (onErrorValue.current === 'post-alert') {
          alert.post({
            severity: 'error',
            message: error.message,
          });
        } else {
          onErrorValue.current?.(error);
        }
      }
    }, [error, alert, onErrorValue]);

    return {
      [dataPath]: create,
      error,
      isCreating,
      called,
      result: error ? undefined : (result as TResult),
    } as StandardCreateHookResult<TResult, TPath, TArgs>;
  };
}
